import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { scanApi } from '../api/httpClient';
import { useAuth } from '../context/AuthContext';

/** V2 SponsorScanner — dark field tool, no framer-motion */

const INTEREST_TAGS = ['Product Demo','Pricing','Partnership','Investment','Recruitment','Other'];

export default function SponsorScanner({ zoneId }) {
  const { logout } = useAuth();
  const navigate   = useNavigate();
  const handleLogout = async () => { try { await logout(); } catch {} navigate('/login'); };
  const [scanning, setScanning]     = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult]         = useState(null);
  const [notes, setNotes]           = useState('');
  const [rating, setRating]         = useState(0);
  const [tags, setTags]             = useState([]);
  const [error, setError]           = useState('');
  const scannerRef = useRef(null);

  const startScanner = async () => {
    setError(''); setResult(null);
    setScanning(true);
    const scanner = new Html5Qrcode('sponsor-qr');
    scannerRef.current = scanner;
    try {
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: 220 },
        handleScan,
        () => {}
      );
    } catch {
      setError('Camera access denied');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleScan = async (token) => {
    if (processing) return;
    setProcessing(true);
    await stopScanner();
    try {
      const data = await scanApi.sponsorScan(token, zoneId, {
        notes, rating: rating || undefined, interest_tags: tags,
      });
      setResult(data);
    } catch (err) {
      setResult({ result: 'DENY', reason: err.message });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => () => stopScanner(), []);

  const toggleTag = (tag) =>
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  /* ── shared dark-field styles ── */
  const s = {
    wrap:    { minHeight: '100vh', background: '#0F0E1A', padding: '20px 16px', color: '#fff' },
    label:   { fontSize: 13, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 6 },
    textarea:{ width: '100%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
               borderRadius: 10, color: '#fff', padding: '10px 14px', fontSize: 14, resize: 'none', outline: 'none', boxSizing: 'border-box' },
    tag:     (on) => ({
      padding: '6px 14px', borderRadius: 50, cursor: 'pointer', fontSize: 13, transition: 'all 0.2s',
      border: `1px solid ${on ? '#C81E78' : 'rgba(255,255,255,0.2)'}`,
      background: on ? 'rgba(200,30,120,0.25)' : 'transparent',
      color: on ? '#F06DAB' : 'rgba(255,255,255,0.6)',
    }),
    scanBtn: { width: '100%', padding: 14, background: 'linear-gradient(115deg,#7B2FE4,#C71D8E,#F5176B)',
               color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
    cancelBtn:{ background: 'none', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)',
                borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 14, marginTop: 16 },
    nextBtn: { marginTop: 24, padding: '12px 32px', background: 'rgba(255,255,255,0.1)',
               border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 10, cursor: 'pointer', fontSize: 15 },
  };

  return (
    <div style={s.wrap}>
      <button onClick={handleLogout} title="Sign out"
        style={{ position: 'fixed', top: 14, right: 14, zIndex: 50,
          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
          color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600 }}>
        ⏋ Sign out
      </button>
      <h2 style={{ textAlign: 'center', marginBottom: 24, fontSize: 22, fontWeight: 700 }}>
        🎯 Sponsor Lead Capture
      </h2>

      {/* ── Pre-scan form ── */}
      {!result && !scanning && (
        <div style={{ maxWidth: 400, margin: '0 auto' }}>
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>Notes (optional)</label>
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="What did you discuss?" rows={3} style={s.textarea}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>Lead Rating</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[1,2,3,4,5].map(star => (
                <button key={star} onClick={() => setRating(star)}
                  style={{ fontSize: 28, background: 'none', border: 'none', cursor: 'pointer',
                           filter: star <= rating ? 'none' : 'grayscale(1) opacity(0.4)',
                           transition: 'filter 0.2s' }}>
                  ⭐
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={s.label}>Interest Tags</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {INTEREST_TAGS.map(tag => (
                <button key={tag} onClick={() => toggleTag(tag)} style={s.tag(tags.includes(tag))}>
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <button onClick={startScanner} style={s.scanBtn}>📷 Scan Attendee QR</button>
          {error && <p style={{ color: '#FF3D57', textAlign: 'center', marginTop: 12 }}>{error}</p>}
        </div>
      )}

      {/* ── Camera view ── */}
      {scanning && (
        <div style={{ maxWidth: 320, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ borderRadius: 16, overflow: 'hidden', border: '2px solid rgba(200,30,120,0.5)', position: 'relative' }}>
            <div id="sponsor-qr" style={{ width: '100%', height: 280 }} />
            {/* scan line */}
            <div style={{
              position: 'absolute', left: 0, right: 0, height: 2, top: '50%',
              background: 'linear-gradient(90deg, transparent, #00D4AA, transparent)',
              animation: 'scanLine 2s ease-in-out infinite',
            }} />
          </div>
          <button onClick={() => { stopScanner(); setProcessing(false); }} style={s.cancelBtn}>
            Cancel
          </button>
        </div>
      )}

      {/* ── Result ── */}
      {result && (
        <div style={{ textAlign: 'center', maxWidth: 320, margin: '0 auto' }}>
          {result.result === 'ALLOW' ? (
            <>
              <div style={{
                width: 100, height: 100, borderRadius: '50%',
                background: 'linear-gradient(115deg,#7B2FE4,#C71D8E,#F5176B)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 48, margin: '0 auto 20px',
              }}>🎯</div>
              <h2 style={{ color: '#00D4AA', fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Lead Captured!</h2>
              <p style={{ color: '#fff', fontSize: 18 }}>{result.attendee_display_name}</p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 64, marginBottom: 16 }}>🚫</div>
              <h2 style={{ color: '#FF3D57', fontSize: 24, fontWeight: 700 }}>Not Captured</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>
                {result.reason === 'CONSENT_DENIED' ? 'Attendee has not consented to lead sharing'
                 : result.reason === 'LEAD_EXISTS'  ? 'You already captured this lead'
                 : result.reason}
              </p>
            </>
          )}
          <button onClick={() => { setResult(null); setNotes(''); setRating(0); setTags([]); }}
            style={s.nextBtn}>
            Scan Next
          </button>
        </div>
      )}

      <style>{`
        @keyframes scanLine {
          0%,100% { top: 5%; }
          50%      { top: 90%; }
        }
      `}</style>
    </div>
  );
}
